<template>
  <div class="i-career-characteristics">
    <template
      v-for="characteristics in characteristicsData"
      :key="characteristics.title"
    >
      <div
        v-if="!!characteristics.percent"
        class="main card"
      >
        <div class="top">
          {{ characteristics.title }}
          <div class="tooltip">
            {{ characteristics.tooltip }}
          </div>
        </div>
        <div class="percent">{{ characteristics.percent }}%</div>
        <div class="range card">
          <div class="markers">
            <div
              class="marker"
              :style="{ backgroundColor: sorbus600 }"
            ></div>
            <div
              class="marker"
              :style="{ backgroundColor: sorbus300 }"
            ></div>
            <div
              class="marker"
              :style="{ backgroundColor: riptide300 }"
            ></div>
          </div>
          <div
            class="pointer"
            :style="{ '--position': `${characteristics.percent}%` }"
          ></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { riptide300, sorbus300, sorbus600 } from "@/styles/colors"

import { getArticle } from "@/composables/getArticle"

// STORES AND COMPOSABLES
const reportStore = useReportStore()

// COMPUTED
const activeJob = computed(() => reportStore.getActiveJob)

const characteristicsData = computed(() => {
  const jobTitle = activeJob.value?.title || ""
  const output = [
    {
      title: "Job Satisfaction",
      percent: activeJob.value?.characteristics.satisfaction,
      tooltip: `To what extent do ${jobTitle} professionals find their jobs rewarding and enjoyable?`
    },
    {
      title: "Job Security",
      percent: activeJob.value?.characteristics.security,
      tooltip: `How secure is ${getArticle(jobTitle)} ${jobTitle} position, especially during economic downturns?`
    },
    {
      title: "Growth Potential",
      percent: activeJob.value?.characteristics.advancement,
      tooltip: `As ${getArticle(
        jobTitle
      )} ${jobTitle}, how likely are you to advance in your career compared to other professions?`
    },
    {
      title: "Work-life Balance",
      percent: activeJob.value?.characteristics.balance,
      tooltip: `How easy is it to balance work and personal life as ${getArticle(jobTitle)} ${jobTitle}?`
    }
  ]
  return output
})
</script>

<style lang="scss" scoped>
.i-career-characteristics {
  @include grid($cols: 1fr, $rows: auto, $gap: 16px);

  margin: 15px 0 20px;

  @media screen and (min-width: $breakpoint-sm) {
    @include grid($cols: 1fr 1fr, $rows: 1fr 1fr, $gap: 16px);
  }

  .card {
    width: 100%;

    @include border-radius-lg;

    border: 1px solid $zumthor100;
    color: $pickle-bluewood900;
  }

  .main {
    @include flex(column, $justify: space-between, $align: center, $gap: 20px);

    padding: 18px;
    min-height: 330px;
    text-align: center;
    font-weight: 700;

    @include text-xl;

    .top {
      margin-top: 16px;
      width: 100%;

      .tooltip {
        @include text-xs;

        font-weight: 500;
        margin-top: 8px;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 54px;
        }
      }
    }

    .percent {
      @include display-lg;
    }

    .range {
      padding: 35px 16px;
      position: relative;

      .markers {
        @include flex(row, $gap: 8px);

        margin: auto;

        .marker {
          width: calc(100% / 3);
          height: 8px;

          @include border-radius-md;

          filter: opacity(60%);
        }
      }

      .pointer {
        position: absolute;
        height: 40px;
        width: 8px;
        background-color: $black;
        border-left: 2px solid $white;
        border-right: 2px solid $white;

        @include border-radius-xs;

        transform: translate(-50%, -58%);
        left: var(--position);
      }
    }
  }
}
</style>
